import projectConfig, { PROJECT } from '~/projects/config';
import justdoneRuntimeBehavior from '~/projects/justdone/runtimeBehavior';
import polyllmChatRuntimeBehavior from '~/projects/polyllm-chat/runtimeBehavior';
import sidekickerRuntimeBehavior from '~/projects/sidekicker/runtimeBehavior';
import {
  PaymentByRegionConfig,
  PaymentConfig,
  PersonalInformationLegalLink,
  ProjectRuntimeBehavior,
} from '~/projects/types';
import { resolvePaymentConfigForCountry } from '~/utils/resolvePaymentConfigForCountry';

export type { PaymentByRegionConfig, PaymentConfig, ProjectRuntimeBehavior };

const BY_PROJECT: Record<PROJECT, ProjectRuntimeBehavior> = {
  [PROJECT.JUSTDONE]: justdoneRuntimeBehavior,
  [PROJECT.SIDEKICKER]: sidekickerRuntimeBehavior,
  [PROJECT.POLYLLM_CHAT]: polyllmChatRuntimeBehavior,
};

/**
 * Per-project runtime behavior so feature code does not scatter
 * `projectConfig.project === PROJECT.*` checks.
 */
export class ProjectBehaviorStrategy {
  static get current(): ProjectRuntimeBehavior {
    return BY_PROJECT[projectConfig.project];
  }

  static get shouldFetchSubscriptionEndpoint(): boolean {
    return this.current.fetchSubscriptionEndpoint;
  }

  static get shouldCreateFunnelUserBeforeSignUp(): boolean {
    return this.current.createFunnelUserBeforeSignUp;
  }

  static get shouldFetchTrustpilotInviteUrl(): boolean {
    return this.current.fetchTrustpilotInviteUrl;
  }

  static get shouldUseProfileSubscriptionOnly(): boolean {
    return this.current.subscriptionFromProfileOnly;
  }

  static get shouldShowSubscriptionUsageBlocks(): boolean {
    return this.current.showSubscriptionUsageBlocks;
  }

  /** OnboardingMiddleware enforces redirect to onboarding when user is not onboarded. */
  static get isWithOnboardingMiddleware(): boolean {
    return this.current.withOnboardingMiddleware;
  }

  static get shouldUseSettingsMainLayoutNoSidebar(): boolean {
    return this.current.settingsMainLayoutNoSidebar;
  }

  static get shouldLoadDesktopSidebarThemeToggle(): boolean {
    return this.current.loadDesktopSidebarThemeToggle;
  }

  static get shouldUseGpt51AsDefaultChatVendor(): boolean {
    return this.current.chatVendorDefaultIsGpt51;
  }

  static get isChatVendorSelectorAvailableWithoutFeatureFlag(): boolean {
    return this.current.chatVendorSelectorAvailableWithoutFeatureFlag;
  }

  static get usesChatGptShellLayout(): boolean {
    return this.current.chatUsesGptShellLayout;
  }

  static get supportsAnonymousChatSessions(): boolean {
    return this.current.chatAnonymousSessionsEnabled;
  }

  static get shouldUseChatwithaiEndpoints(): boolean {
    return this.current.chatUsesChatwithaiEndpoints;
  }

  static get siteTitle(): string {
    return this.current.title;
  }

  static get shouldShowFrozenSubscriptionBlock(): boolean {
    return this.current.showFrozenSubscriptionBlock;
  }

  static get shouldShowHomeRetentionPopover(): boolean {
    return this.current.showHomeRetentionPopover;
  }

  static get shouldShowTrustpilotMentions(): boolean {
    return this.current.showTrustpilotMentions;
  }

  static get shouldOpenTrustpilotOnChatLike(): boolean {
    return this.current.openTrustpilotOnChatLike;
  }

  static get backendErrorsHandlingStrategy(): ProjectRuntimeBehavior['backendErrorsHandlingStrategy'] {
    return this.current.backendErrorsHandlingStrategy;
  }

  static get shouldSendDeviceIdOnSignUp(): boolean {
    return this.current.signUpUsesDeviceIdHeader;
  }

  static get shouldShowNoActiveSubscriptionModal(): boolean {
    return this.current.showNoActiveSubscriptionModal;
  }

  static get usesPolyllmRedemptionCheckout(): boolean {
    return this.current.usesPolyllmRedemptionCheckout;
  }

  static getPersonalInformationLegalLinksByCountry(
    countryCode: string,
  ): ReadonlyArray<PersonalInformationLegalLink> {
    const linksByRegion = this.current.personalInformationLegalLinksByRegion;

    return linksByRegion[countryCode] || linksByRegion.default;
  }

  static getPaymentConfigByCountry(countryCode: string): PaymentConfig {
    return resolvePaymentConfigForCountry(
      this.current.paymentConfigByRegion,
      countryCode,
    );
  }
}
